/**
 * DashboardPage - Main dashboard with risk posture and actionable insights.
 * Phase 3: Dashboard Implementation
 * 
 * Shows engineers within 10 seconds:
 * - Overall risk posture
 * - Critical/high findings needing attention
 * - Audit health/progress
 * - Recent activity
 * - Recommended next action
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, AuditSummaryStats, RecentAudit } from '../lib/api'
import {
  getAttentionItems,
  calculateRiskPosture,
  getRecommendedAction,
  getSeverityDistribution,
  isFirstUseState,
  formatRelativeTime,
  type RecommendedAction,
} from '../lib/dashboardHelpers'
import { LoadingSpinner } from '../components/design-system/Loading'
import { EmptyState } from '../components/design-system/EmptyState'
import { Badge, SeverityBadge, StatusBadge } from '../components/design-system/Badge'
import { Button } from '../components/design-system/Button'
import { StartAuditDialog } from '../components/StartAuditDialog'
import { useLocale } from '../contexts/LocaleContext'
import { severityLabel } from '../lib/i18n'

function localizeRecommended(
  action: RecommendedAction,
  stats: AuditSummaryStats,
  t: (key: string, vars?: Record<string, string | number>) => string,
): RecommendedAction {
  const link = action.actionLink
  if (link.includes('severity=critical')) {
    return {
      ...action,
      title: t('rec.criticalTitle'),
      description: t('rec.criticalBody', { n: stats.critical_findings }),
      actionLabel: t('rec.criticalAction'),
      reasoning: t('rec.criticalWhy'),
    }
  }
  if (link.includes('status=failed') || (link.startsWith('/audits') && action.title.toLowerCase().includes('failed'))) {
    const n = Number(action.description) || 1
    return {
      ...action,
      title: t('rec.failedTitle'),
      description: t('rec.failedBody', { n }),
      actionLabel: t('rec.failedAction'),
      reasoning: t('rec.failedWhy'),
    }
  }
  if (link.includes('severity=high')) {
    return {
      ...action,
      title: t('rec.highTitle'),
      description: t('rec.highBody', { n: stats.high_findings }),
      actionLabel: t('rec.highAction'),
      reasoning: t('rec.highWhy'),
    }
  }
  if (link === '/audits' && action.title.toLowerCase().includes('monitor')) {
    return {
      ...action,
      title: t('rec.runningTitle'),
      description: t('rec.runningBody', { n: stats.running_audits }),
      actionLabel: t('rec.runningAction'),
      reasoning: t('rec.runningWhy'),
    }
  }
  if (link === '/projects') {
    return {
      ...action,
      title: t('rec.firstTitle'),
      description: t('rec.firstBody'),
      actionLabel: t('rec.firstAction'),
      reasoning: t('rec.firstWhy'),
    }
  }
  if (link.includes('severity=medium')) {
    return {
      ...action,
      title: t('rec.mediumTitle'),
      description: t('rec.mediumBody', { n: stats.medium_findings }),
      actionLabel: t('rec.mediumAction'),
      reasoning: t('rec.mediumWhy'),
    }
  }
  if (link === '/findings' || link.startsWith('/findings?status=open')) {
    return {
      ...action,
      title: t('rec.allTitle'),
      description: t('rec.allBody', { n: stats.total_findings }),
      actionLabel: t('rec.allAction'),
      reasoning: t('rec.allWhy'),
    }
  }
  return action
}

export function DashboardPage() {
  const navigate = useNavigate()
  const { t, locale } = useLocale()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [stats, setStats] = useState<AuditSummaryStats | null>(null)
  const [recentAudits, setRecentAudits] = useState<RecentAudit[]>([])
  const [startOpen, setStartOpen] = useState(false)
  
  useEffect(() => {
    loadDashboardData()
  }, [])
  
  async function loadDashboardData() {
    try {
      setLoading(true)
      setError(null)
      
      const [statsData, auditsData] = await Promise.all([
        api.getAuditSummary(),
        api.getRecentAudits(20),
      ])
      
      setStats(statsData)
      setRecentAudits(auditsData)
    } catch (err) {
        setError(err instanceof Error ? err.message : t('dash.failedLoadFallback'))
    } finally {
      setLoading(false)
    }
  }
  
  // Calculate derived data
  const attentionItemsRaw = stats && recentAudits ? getAttentionItems(recentAudits) : []
  const attentionItems = locale === 'ru'
    ? attentionItemsRaw.map((item) => {
        if (item.status === 'failed') {
          return {
            ...item,
            title: t('attention.failedTitle', { project: item.project_name }),
            description: t('attention.failedBody'),
            actionLabel: t('attention.open'),
          }
        }
        if (item.riskLevel === 'critical') {
          return {
            ...item,
            title: t('attention.criticalTitle', { n: item.critical_count, project: item.project_name }),
            description: t('attention.criticalBody'),
            actionLabel: t('attention.open'),
          }
        }
        if (item.riskLevel === 'high') {
          return {
            ...item,
            title: t('attention.highTitle', { n: item.high_count, project: item.project_name }),
            description: t('attention.highBody'),
            actionLabel: t('attention.open'),
          }
        }
        return {
          ...item,
          title: t('attention.otherTitle', { project: item.project_name }),
          description: t('attention.otherBody', { n: item.total_findings }),
          actionLabel: t('attention.open'),
        }
      })
    : attentionItemsRaw
  const riskPosture = stats ? calculateRiskPosture(stats) : null
  const recommendedActionRaw = stats ? getRecommendedAction(stats, recentAudits) : null
  const recommendedAction = recommendedActionRaw && stats
    ? localizeRecommended(recommendedActionRaw, stats, t)
    : null
  const severityDist = stats ? getSeverityDistribution(stats) : null
  const isFirstUse = stats ? isFirstUseState(stats) : false

  // Loading state
  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <h1
          style={{
            margin: 0,
            fontFamily: 'var(--reviewer-font-mono)',
            fontSize: 'var(--reviewer-text-2xl)',
            fontWeight: 500,
            color: 'var(--reviewer-text)',
          }}
        >
            {t('dash.title')}
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '320px' }}>
          <LoadingSpinner size="lg" text={t('dash.loading')} />
        </div>
      </div>
    )
  }
  
  // Error state with retry
  if (error) {
    return (
      <div
        style={{
          maxWidth: '600px',
          margin: '0 auto',
          padding: '32px',
        }}
      >
        <h1
          style={{
            margin: 0,
            marginBottom: '16px',
            fontFamily: 'var(--reviewer-font-mono)',
            fontSize: 'var(--reviewer-text-2xl)',
            fontWeight: 500,
            color: 'var(--reviewer-text)',
            textAlign: 'center',
          }}
        >
            {t('dash.title')}
        </h1>
        <div
          style={{
            border: '1px solid var(--reviewer-danger-border)',
            backgroundColor: 'var(--reviewer-danger-soft)',
            padding: '24px',
          }}
        >
          <h2 style={{ 
            margin: 0, 
            marginBottom: '12px',
            fontFamily: 'var(--reviewer-font-mono)',
            fontSize: 'var(--reviewer-text-lg)',
            color: 'var(--reviewer-danger)',
          }}>
            {t('dash.loadFailed')}
          </h2>
          <p style={{ 
            margin: 0, 
            marginBottom: '16px',
            fontFamily: 'var(--reviewer-font-mono)',
            fontSize: 'var(--reviewer-text-sm)',
            color: 'var(--reviewer-text)',
          }}>
            {error}
          </p>
          <Button onClick={loadDashboardData} variant="danger">
            {t('common.retry')}
          </Button>
        </div>
      </div>
    )
  }
  
  // No data state (shouldn't happen if API is working)
  if (!stats) {
    return (
      <EmptyState 
        title={t('dash.noData')} 
        description={t('dash.noDataBody')}
      />
    )
  }

  // First-use empty state
  if (isFirstUse) {
    return (
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '32px 0' }}>
        <div
          style={{
            border: '1px solid var(--reviewer-border)',
            backgroundColor: 'var(--reviewer-surface)',
            padding: '48px 32px',
            textAlign: 'center',
          }}
        >
          <h1
            style={{
              margin: 0,
              marginBottom: '16px',
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-2xl)',
              color: 'var(--reviewer-text)',
            }}
          >
              {t('dash.title')}
          </h1>
          <p
            style={{
              margin: 0,
              marginBottom: '32px',
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-base)',
              color: 'var(--reviewer-text-secondary)',
            }}
          >
            {t('dash.welcome')}
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button 
              onClick={() => setStartOpen(true)} 
              variant="primary"
              size="lg"
            >
              {t('dash.startFirst')}
            </Button>
            <Button 
              onClick={() => navigate('/projects')} 
              variant="secondary"
              size="lg"
            >
              {t('dash.addProject')}
            </Button>
          </div>
          <StartAuditDialog isOpen={startOpen} onClose={() => setStartOpen(false)} />
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* 1. Header & Context */}
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
        <div>
          <h1
            style={{
              margin: 0,
              marginBottom: '8px',
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-2xl)',
              fontWeight: 500,
              color: 'var(--reviewer-text)',
            }}
          >
              {t('dash.title')}
          </h1>
          <p
            style={{
              margin: 0,
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-base)',
              color: 'var(--reviewer-text-secondary)',
            }}
          >
            {t('dash.lead')}
          </p>
        </div>
        <Button onClick={() => setStartOpen(true)} data-testid="dashboard-start-audit">
          {t('dash.startAuditBtn')}
        </Button>
      </header>

      {/* 2. Risk Posture / KPI Strip */}
      <section aria-label={t('dash.riskAria')}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: '16px',
          }}
        >
          {/* {t('dash.overallRisk')} */}
          <div
            style={{
              border: '1px solid var(--reviewer-border)',
              backgroundColor: 'var(--reviewer-surface)',
              padding: '20px',
            }}
          >
            <div style={{ 
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-xs)',
              fontWeight: 500,
              color: 'var(--reviewer-text-tertiary)',
              textTransform: 'uppercase',
              marginBottom: '8px',
            }}>
              {t('dash.overallRisk')}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <Badge 
                variant={riskPosture?.level === 'critical' ? 'danger' : riskPosture?.level === 'high' ? 'warning' : 'success'} 
                size="md"
              >
                {riskPosture ? severityLabel(locale, riskPosture.level) : '—'}
              </Badge>
              {riskPosture && riskPosture.score > 0 && (
                <span style={{ 
                  fontFamily: 'var(--reviewer-font-mono)',
                  fontSize: 'var(--reviewer-text-xl)',
                  fontWeight: 600,
                  color: 'var(--reviewer-text)',
                }}>
                  {riskPosture.score}/100
                </span>
              )}
            </div>
            <p style={{ 
              margin: 0,
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-sm)',
              color: 'var(--reviewer-text-secondary)',
            }}>
              {riskPosture
                ? riskPosture.level === 'critical'
                  ? t('dash.postureCritical', { n: riskPosture.criticalIssues })
                  : riskPosture.level === 'high'
                    ? t('dash.postureHigh', { n: riskPosture.highIssues })
                    : t('dash.postureLow')
                : ''}
            </p>
          </div>

          {/* Critical & High Findings */}
          <div
            style={{
              border: '1px solid var(--reviewer-border)',
              backgroundColor: 'var(--reviewer-surface)',
              padding: '20px',
              cursor: stats.critical_findings + stats.high_findings > 0 ? 'pointer' : 'default',
              transition: 'border-color 0.2s',
            }}
            onClick={() => {
              if (stats.critical_findings + stats.high_findings > 0) {
                navigate('/findings?severity=critical,high')
              }
            }}
            onMouseEnter={(e) => {
              if (stats.critical_findings + stats.high_findings > 0) {
                e.currentTarget.style.borderColor = 'var(--reviewer-accent)'
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--reviewer-border)'
            }}
            role={stats.critical_findings + stats.high_findings > 0 ? 'link' : undefined}
            tabIndex={stats.critical_findings + stats.high_findings > 0 ? 0 : undefined}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && stats.critical_findings + stats.high_findings > 0) {
                e.preventDefault()
                navigate('/findings?severity=critical,high')
              }
            }}
            aria-label={stats.critical_findings + stats.high_findings > 0 ? t('dash.viewCritHigh') : undefined}
          >
            <div style={{ 
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-xs)',
              fontWeight: 500,
              color: 'var(--reviewer-text-tertiary)',
              textTransform: 'uppercase',
              marginBottom: '8px',
            }}>
              {t('dash.critHigh')}
            </div>
            <div style={{ 
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-3xl)',
              fontWeight: 600,
              color: stats.critical_findings > 0 ? 'var(--reviewer-danger)' : 'var(--reviewer-text)',
              marginBottom: '8px',
            }}>
              {stats.critical_findings + stats.high_findings}
            </div>
            <div style={{ 
              display: 'flex',
              gap: '12px',
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-sm)',
            }}>
              <span style={{ color: 'var(--reviewer-danger)' }}>
                {stats.critical_findings} {t('dash.critShort')}
              </span>
              <span style={{ color: 'var(--reviewer-warning)' }}>
                {stats.high_findings} {t('dash.highShort')}
              </span>
            </div>
            {stats.critical_findings + stats.high_findings > 0 && (
              <div style={{ 
                marginTop: '8px',
                fontFamily: 'var(--reviewer-font-mono)',
                fontSize: 'var(--reviewer-text-xs)',
                color: 'var(--reviewer-accent)',
              }}>
                {t('dash.clickView')}
              </div>
            )}
          </div>

          {/* Audit Status */}
          <div
            style={{
              border: '1px solid var(--reviewer-border)',
              backgroundColor: 'var(--reviewer-surface)',
              padding: '20px',
            }}
          >
            <div style={{ 
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-xs)',
              fontWeight: 500,
              color: 'var(--reviewer-text-tertiary)',
              textTransform: 'uppercase',
              marginBottom: '8px',
            }}>
              {t('dash.auditHealth')}
            </div>
            <div style={{ 
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-3xl)',
              fontWeight: 600,
              color: 'var(--reviewer-text)',
              marginBottom: '8px',
            }}>
              {stats.total_audits}
            </div>
            <div style={{ 
              display: 'flex',
              gap: '12px',
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-sm)',
              flexWrap: 'wrap',
            }}>
              <span style={{ color: 'var(--reviewer-text-secondary)' }}>
                {stats.total_projects ?? 0} {t('dash.projectsShort')}
              </span>
              <span style={{ color: 'var(--reviewer-success)' }}>
                {stats.completed_audits} {t('dash.doneShort')}
              </span>
              {stats.running_audits > 0 && (
                <span style={{ color: 'var(--reviewer-info)' }}>
                  {stats.running_audits} {t('dash.runningShort')}
                </span>
              )}
              {stats.failed_audits > 0 && (
                <span style={{ color: 'var(--reviewer-danger)' }}>
                  {stats.failed_audits} {t('dash.failedShort')}
                </span>
              )}
            </div>
          </div>

          {/* {t('dash.totalFindingsKpi')} */}
          <div
            style={{
              border: '1px solid var(--reviewer-border)',
              backgroundColor: 'var(--reviewer-surface)',
              padding: '20px',
              cursor: stats.total_findings > 0 ? 'pointer' : 'default',
              transition: 'border-color 0.2s',
            }}
            onClick={() => {
              if (stats.total_findings > 0) {
                navigate('/findings')
              }
            }}
            onMouseEnter={(e) => {
              if (stats.total_findings > 0) {
                e.currentTarget.style.borderColor = 'var(--reviewer-accent)'
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--reviewer-border)'
            }}
            role={stats.total_findings > 0 ? 'link' : undefined}
            tabIndex={stats.total_findings > 0 ? 0 : undefined}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && stats.total_findings > 0) {
                e.preventDefault()
                navigate('/findings')
              }
            }}
            aria-label={stats.total_findings > 0 ? t('dash.viewAllFindings') : undefined}
          >
            <div style={{ 
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-xs)',
              fontWeight: 500,
              color: 'var(--reviewer-text-tertiary)',
              textTransform: 'uppercase',
              marginBottom: '8px',
            }}>
              {t('dash.totalFindingsKpi')}
            </div>
            <div style={{ 
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-3xl)',
              fontWeight: 600,
              color: 'var(--reviewer-text)',
              marginBottom: '8px',
            }}>
              {stats.total_findings}
            </div>
            <p style={{ 
              margin: 0,
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-sm)',
              color: 'var(--reviewer-text-secondary)',
            }}>
              {t('dash.acrossAudits')}
            </p>
            {stats.total_findings > 0 && (
              <div style={{ 
                marginTop: '8px',
                fontFamily: 'var(--reviewer-font-mono)',
                fontSize: 'var(--reviewer-text-xs)',
                color: 'var(--reviewer-accent)',
              }}>
                {t('dash.clickView')}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* 3. Attention Queue + 5. Risk Distribution (side by side) */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)',
          gap: '24px',
        }}
      >
        {/* 3. Attention Queue */}
        <section
          aria-label={t('dash.attentionAria')}
          style={{
            border: '1px solid var(--reviewer-border)',
            backgroundColor: 'var(--reviewer-surface)',
            padding: '24px',
          }}
        >
          <h2
            style={{
              margin: 0,
              marginBottom: '16px',
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-lg)',
              fontWeight: 500,
              color: 'var(--reviewer-text)',
            }}
          >
            {t('dash.requiresAttention')}
          </h2>
          {attentionItems.length === 0 ? (
            <div style={{ padding: '32px 0', textAlign: 'center' }}>
              <p style={{ 
                margin: 0,
                fontFamily: 'var(--reviewer-font-mono)',
                fontSize: 'var(--reviewer-text-base)',
                color: 'var(--reviewer-text-secondary)',
              }}>
                {t('dash.noAttention')}
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {attentionItems.slice(0, 5).map((item) => (
                <div
                  key={item.id}
                  onClick={() => navigate(item.actionLink)}
                  style={{
                    border: '1px solid var(--reviewer-border)',
                    backgroundColor: 'var(--reviewer-bg-elevated)',
                    padding: '16px',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--reviewer-accent)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--reviewer-border)'
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      navigate(item.actionLink)
                    }
                  }}
                  aria-label={`${item.title} - ${item.description}`}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ 
                        fontFamily: 'var(--reviewer-font-mono)',
                        fontSize: 'var(--reviewer-text-base)',
                        fontWeight: 500,
                        color: 'var(--reviewer-text)',
                        marginBottom: '4px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {item.title}
                      </div>
                      <div style={{ 
                        fontFamily: 'var(--reviewer-font-mono)',
                        fontSize: 'var(--reviewer-text-sm)',
                        color: 'var(--reviewer-text-secondary)',
                      }}>
                        {item.description}
                      </div>
                    </div>
                    <Badge 
                      variant={item.riskLevel === 'critical' ? 'danger' : item.riskLevel === 'high' ? 'warning' : 'info'} 
                      size="sm"
                    >
                      {severityLabel(locale, item.riskLevel)}
                    </Badge>
                  </div>
                  <div style={{ 
                    fontFamily: 'var(--reviewer-font-mono)',
                    fontSize: 'var(--reviewer-text-xs)',
                    color: 'var(--reviewer-accent)',
                    textDecoration: 'underline',
                  }}>
                    {item.actionLabel} →
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* 5. Risk Distribution */}
        <section
          aria-label={t('dash.distAria')}
          style={{
            border: '1px solid var(--reviewer-border)',
            backgroundColor: 'var(--reviewer-surface)',
            padding: '24px',
          }}
        >
          <h2
            style={{
              margin: 0,
              marginBottom: '16px',
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-lg)',
              fontWeight: 500,
              color: 'var(--reviewer-text)',
            }}
          >
            {t('dash.distribution')}
          </h2>
          {severityDist && severityDist.total > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Horizontal bar chart */}
              <div>
                <div style={{ display: 'flex', height: '32px', border: '1px solid var(--reviewer-border)' }}>
                  {severityDist.critical > 0 && (
                    <div
                      style={{
                        width: `${(severityDist.critical / severityDist.total) * 100}%`,
                        backgroundColor: 'var(--reviewer-danger)',
                      }}
                      title={`${severityDist.critical} Critical`}
                      aria-label={`${severityDist.critical} critical findings, ${Math.round((severityDist.critical / severityDist.total) * 100)}%`}
                    />
                  )}
                  {severityDist.high > 0 && (
                    <div
                      style={{
                        width: `${(severityDist.high / severityDist.total) * 100}%`,
                        backgroundColor: 'var(--reviewer-warning)',
                      }}
                      title={`${severityDist.high} High`}
                      aria-label={`${severityDist.high} high findings, ${Math.round((severityDist.high / severityDist.total) * 100)}%`}
                    />
                  )}
                  {severityDist.medium > 0 && (
                    <div
                      style={{
                        width: `${(severityDist.medium / severityDist.total) * 100}%`,
                        backgroundColor: 'var(--reviewer-info)',
                      }}
                      title={`${severityDist.medium} Medium`}
                      aria-label={`${severityDist.medium} medium findings, ${Math.round((severityDist.medium / severityDist.total) * 100)}%`}
                    />
                  )}
                  {severityDist.low > 0 && (
                    <div
                      style={{
                        width: `${(severityDist.low / severityDist.total) * 100}%`,
                        backgroundColor: 'var(--reviewer-success)',
                      }}
                      title={`${severityDist.low} Low`}
                      aria-label={`${severityDist.low} low findings, ${Math.round((severityDist.low / severityDist.total) * 100)}%`}
                    />
                  )}
                  {severityDist.info > 0 && (
                    <div
                      style={{
                        width: `${(severityDist.info / severityDist.total) * 100}%`,
                        backgroundColor: 'var(--reviewer-bg-elevated)',
                      }}
                      title={`${severityDist.info} Info`}
                      aria-label={`${severityDist.info} info findings, ${Math.round((severityDist.info / severityDist.total) * 100)}%`}
                    />
                  )}
                </div>
              </div>

              {/* Legend */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {severityDist.critical > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '12px', height: '12px', backgroundColor: 'var(--reviewer-danger)' }} />
                      <span style={{ fontFamily: 'var(--reviewer-font-mono)', fontSize: 'var(--reviewer-text-sm)', color: 'var(--reviewer-text)' }}>
                        {t('dash.legendCritical')}
                      </span>
                    </div>
                    <span style={{ fontFamily: 'var(--reviewer-font-mono)', fontSize: 'var(--reviewer-text-sm)', fontWeight: 600, color: 'var(--reviewer-danger)' }}>
                      {severityDist.critical}
                    </span>
                  </div>
                )}
                {severityDist.high > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '12px', height: '12px', backgroundColor: 'var(--reviewer-warning)' }} />
                      <span style={{ fontFamily: 'var(--reviewer-font-mono)', fontSize: 'var(--reviewer-text-sm)', color: 'var(--reviewer-text)' }}>
                        {t('dash.legendHigh')}
                      </span>
                    </div>
                    <span style={{ fontFamily: 'var(--reviewer-font-mono)', fontSize: 'var(--reviewer-text-sm)', fontWeight: 600, color: 'var(--reviewer-warning)' }}>
                      {severityDist.high}
                    </span>
                  </div>
                )}
                {severityDist.medium > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '12px', height: '12px', backgroundColor: 'var(--reviewer-info)' }} />
                      <span style={{ fontFamily: 'var(--reviewer-font-mono)', fontSize: 'var(--reviewer-text-sm)', color: 'var(--reviewer-text)' }}>
                        {t('dash.legendMedium')}
                      </span>
                    </div>
                    <span style={{ fontFamily: 'var(--reviewer-font-mono)', fontSize: 'var(--reviewer-text-sm)', fontWeight: 600, color: 'var(--reviewer-info)' }}>
                      {severityDist.medium}
                    </span>
                  </div>
                )}
                {severityDist.low > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '12px', height: '12px', backgroundColor: 'var(--reviewer-success)' }} />
                      <span style={{ fontFamily: 'var(--reviewer-font-mono)', fontSize: 'var(--reviewer-text-sm)', color: 'var(--reviewer-text)' }}>
                        {t('dash.legendLow')}
                      </span>
                    </div>
                    <span style={{ fontFamily: 'var(--reviewer-font-mono)', fontSize: 'var(--reviewer-text-sm)', fontWeight: 600, color: 'var(--reviewer-success)' }}>
                      {severityDist.low}
                    </span>
                  </div>
                )}
                {severityDist.info > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '12px', height: '12px', backgroundColor: 'var(--reviewer-bg-elevated)', border: '1px solid var(--reviewer-border)' }} />
                      <span style={{ fontFamily: 'var(--reviewer-font-mono)', fontSize: 'var(--reviewer-text-sm)', color: 'var(--reviewer-text)' }}>
                        {t('dash.legendInfo')}
                      </span>
                    </div>
                    <span style={{ fontFamily: 'var(--reviewer-font-mono)', fontSize: 'var(--reviewer-text-sm)', fontWeight: 600, color: 'var(--reviewer-text-secondary)' }}>
                      {severityDist.info}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div style={{ padding: '32px 0', textAlign: 'center' }}>
              <p style={{ 
                margin: 0,
                fontFamily: 'var(--reviewer-font-mono)',
                fontSize: 'var(--reviewer-text-base)',
                color: 'var(--reviewer-text-secondary)',
              }}>
                {t('dash.noFindingsDisplay')}
              </p>
            </div>
          )}
        </section>
      </div>

      {/* 7. Recommended Next Action */}
      {recommendedAction && (
        <section
          aria-label={t('dash.recAria')}
          style={{
            border: `2px solid ${recommendedAction.priority === 'high' ? 'var(--reviewer-danger-border)' : recommendedAction.priority === 'medium' ? 'var(--reviewer-warning-border)' : 'var(--reviewer-accent-border)'}`,
            backgroundColor: recommendedAction.priority === 'high' ? 'var(--reviewer-danger-soft)' : recommendedAction.priority === 'medium' ? 'var(--reviewer-warning-soft)' : 'var(--reviewer-accent-soft)',
            padding: '24px',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <Badge 
                  variant={recommendedAction.priority === 'high' ? 'danger' : recommendedAction.priority === 'medium' ? 'warning' : 'accent'} 
                  size="sm"
                >
                  {t('dash.recommendedBadge')}
                </Badge>
                <h2
                  style={{
                    margin: 0,
                    fontFamily: 'var(--reviewer-font-mono)',
                    fontSize: 'var(--reviewer-text-lg)',
                    fontWeight: 500,
                    color: 'var(--reviewer-text)',
                  }}
                >
                  {recommendedAction.title}
                </h2>
              </div>
              <p style={{ 
                margin: 0,
                marginBottom: '8px',
                fontFamily: 'var(--reviewer-font-mono)',
                fontSize: 'var(--reviewer-text-base)',
                color: 'var(--reviewer-text)',
              }}>
                {recommendedAction.description}
              </p>
              <p style={{ 
                margin: 0,
                fontFamily: 'var(--reviewer-font-mono)',
                fontSize: 'var(--reviewer-text-sm)',
                color: 'var(--reviewer-text-secondary)',
                fontStyle: 'italic',
              }}>
                {recommendedAction.reasoning}
              </p>
            </div>
            <Button
              onClick={() => navigate(recommendedAction.actionLink)}
              variant={recommendedAction.priority === 'high' ? 'danger' : 'primary'}
            >
              {recommendedAction.actionLabel}
            </Button>
          </div>
        </section>
      )}

      {/* 4. Recent Audits/Activity */}
      <section
        aria-label={t('dash.recentAria')}
        style={{
          border: '1px solid var(--reviewer-border)',
          backgroundColor: 'var(--reviewer-surface)',
          padding: '24px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2
            style={{
              margin: 0,
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-lg)',
              fontWeight: 500,
              color: 'var(--reviewer-text)',
            }}
          >
            {t('dash.recentActivity')}
          </h2>
          <button
            onClick={() => navigate('/audits')}
            style={{
              background: 'none',
              border: 'none',
              padding: '4px 8px',
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-sm)',
              color: 'var(--reviewer-accent)',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
            aria-label={t('dash.viewAllAudits')}
          >
            {t('dash.viewAll')}
          </button>
        </div>
        {recentAudits.length === 0 ? (
          <div style={{ padding: '32px 0', textAlign: 'center' }}>
            <p style={{ 
              margin: 0,
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-base)',
              color: 'var(--reviewer-text-secondary)',
            }}>
              {t('dash.noRecent')}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {recentAudits.slice(0, 8).map((audit) => (
              <div
                key={audit.id}
                onClick={() => navigate(`/audit/${audit.id}`)}
                style={{
                  border: '1px solid var(--reviewer-border-subtle)',
                  backgroundColor: 'var(--reviewer-bg-elevated)',
                  padding: '12px 16px',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s',
                  display: 'grid',
                  gridTemplateColumns: '1fr auto auto',
                  gap: '16px',
                  alignItems: 'center',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--reviewer-accent)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--reviewer-border-subtle)'
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    navigate(`/audit/${audit.id}`)
                  }
                }}
                aria-label={`Audit for ${audit.project_name}, status ${audit.status}, ${audit.total_findings} findings`}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ 
                    fontFamily: 'var(--reviewer-font-mono)',
                    fontSize: 'var(--reviewer-text-base)',
                    fontWeight: 500,
                    color: 'var(--reviewer-text)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    marginBottom: '4px',
                  }}>
                    {audit.project_name}
                  </div>
                  <div style={{ 
                    fontFamily: 'var(--reviewer-font-mono)',
                    fontSize: 'var(--reviewer-text-xs)',
                    color: 'var(--reviewer-text-tertiary)',
                  }}>
                    {formatRelativeTime(audit.created_at, locale)}
                    {' • '}
                    {audit.total_findings} {audit.total_findings !== 1 ? t('dash.findingsMany') : t('dash.findingOne')}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {audit.critical_count > 0 && (
                    <SeverityBadge severity="critical" size="sm" />
                  )}
                  {audit.high_count > 0 && (
                    <SeverityBadge severity="high" size="sm" />
                  )}
                </div>
                <div>
                  <StatusBadge status={audit.status} size="sm" />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
      <StartAuditDialog isOpen={startOpen} onClose={() => setStartOpen(false)} />
    </div>
  )
}
